/**
 * A "Export CSV" button for a filtered list (F18).
 *
 * Two rules it enforces in the interface:
 *
 *   - **Hidden from a Viewer.** PRD §4 denies a Viewer the bulk export, and the
 *     server enforces it — but showing a button that only ever 403s is a worse
 *     experience than not showing it. `can('EDITOR')` gates the render; the
 *     server remains the real control.
 *   - **The current filters, not all rows.** The caller passes the same query
 *     object the list is showing, so the file matches the screen.
 *
 * The fetch goes through the authenticated client (the access token lives in
 * memory, so a plain link would download an unauthenticated 401 page), then the
 * blob is saved. A failure is surfaced inline rather than thrown, because a
 * failed export must not blank the list the user is still working with.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.4, PRD F18, §4
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../lib/auth-context';
import { fetchCsv, saveCsv, type ExportEntity } from '../../lib/export.api';

import { Button } from './Button';

export interface ExportButtonProps {
  /** Which list to export. */
  entity: ExportEntity;
  /** The filters the list is currently showing — the export mirrors them. */
  query: Record<string, string | number | boolean | null | undefined>;
}

export function ExportButton({ entity, query }: ExportButtonProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [failed, setFailed] = useState(false);

  /* A Viewer cannot export (PRD §4); hide the control rather than show one that
     only refuses. */
  if (!can('EDITOR')) {
    return null;
  }

  async function handleClick(): Promise<void> {
    setIsExporting(true);
    setFailed(false);
    try {
      saveCsv(await fetchCsv(entity, query));
    } catch {
      /* Any failure — an API error, a network drop — surfaces inline. The list
         behind the button is untouched, so a failed export is a retry, not a
         lost screen. */
      setFailed(true);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        disabled={isExporting}
        onClick={() => {
          void handleClick();
        }}
      >
        {isExporting ? t('export.exporting') : t('export.csv')}
      </Button>
      {failed && (
        <span role="alert" className="text-sem-bad text-xs">
          {t('export.failed')}
        </span>
      )}
    </div>
  );
}
